import React, { useEffect, useState } from 'react';
import CommonItemsMH from '../prescriptionPad/rightPanel/workspace/padV2/CommonItemsMH';
import { MEDICAL_HISTORY_CATEGORY, MHC, MHItem2 } from '../../store/types/medicalHistory';
import { track } from '../../tracking';
import { cloneAndDelete, cloneAndUpsert } from '../../store/utils';
import { medicalHistoryOptions } from '../medicalHistoryConfigPanel/CustomiseSections';
import { MISC_KEYS, getDoc, getItemFromMisc } from '../../store/idbHandler';
import fetchPtMedHistory from '../../api/fetchPtMedHistory';
import { captureError } from '../../pages/captureError';
import { MEDICAL_HISTORY_CONTAINER } from '../prescriptionPad/rightPanel/workspace/MedicalHistoryContainer';
import pushPtMedHistory from '../../api/pushPtMedHistory';
import { Checkbox, notification } from 'antd';
import { AbhaFilled, crossIcon } from './Icons';
import { dispatch as ekaDispatch } from '../../bridge/events/dispatch';
import { isMHFilled } from '../../store/selectors/prescriptionPad';
import { ADD_MH_QUEUE, CLOSE_MH_POP } from '../../bridge/events/types';
import { MedicalHistoryConfig } from '../../api/pushGenie';
import { DEFAULT_MEDICAL_HISTORY_CONFIG_VALUES } from '../../constants';
import { getMyIdWithBackwardCompatibility } from '../../api/mactiveid';

const StatusMessage: {
    [key: string]: string;
} = {
    active: 'Y',
    inactive: 'N',
};

const MHPopup: React.FC<{ patient_oid: string }> = ({ patient_oid }) => {
    const [ptMHHistory, setPtMHHistory] = useState<MHC | undefined>({});
    const [hasNoMedicalHistory, setHasNoMedicalHistory] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [medicalHistoryConfigData, setMedicalHistoryConfigData] = useState<MedicalHistoryConfig>(
        DEFAULT_MEDICAL_HISTORY_CONFIG_VALUES,
    );

    const fetchMH = async (ptid: string) => {
        try {
            const doc = getDoc();
            const medicalHistory = await fetchPtMedHistory(doc?._id ?? '', ptid);

            return medicalHistory.data;
        } catch (error) {
            throw error;
        }
    };

    const getMedicalHistoryConfigData = () => {
        const myId = getMyIdWithBackwardCompatibility() || '';
        getItemFromMisc<MedicalHistoryConfig>(myId, MISC_KEYS.medicalHistoryConfig)
            .then((medicalHistoryConfigData) => {
                setMedicalHistoryConfigData(medicalHistoryConfigData);
            })
            .catch(console.error);
    };

    useEffect(getMedicalHistoryConfigData, []);

    useEffect(() => {
        if (patient_oid) {
            fetchMH(patient_oid)
                .then((ptMH) => {
                    setPtMHHistory(ptMH);
                })
                .catch((error) => {
                    console.log(error);
                    captureError(error as Error);
                });
        }
    }, []);

    const onMedItemSaveToState = (
        catId: MEDICAL_HISTORY_CATEGORY,
        item: MHItem2,
        index?: number,
    ): void => {
        track('MH_ITEMS_SAVE', {
            from: 'pt-mh-popup-component',
            catId,
            item: item.id,
            status: item.status,
            name: item.name,
        });

        if (ptMHHistory) {
            setPtMHHistory((prev) => {
                return {
                    ...prev,
                    [catId]: cloneAndUpsert(item, ptMHHistory[catId], index),
                };
            });
        }
    };

    const onMedItemSave = (catId: MEDICAL_HISTORY_CATEGORY, item: MHItem2, index?: number) => {
        const { status } = item;

        if (status) {
            catId && onMedItemSaveToState(catId, item, index);
        } else {
            catId && onMedItemSaveToState(catId, { ...item, status: 'Active' }, index);
        }
    };

    const onMedItemDelete = (catId: MEDICAL_HISTORY_CATEGORY, itemId: string): void => {
        track('MH_ITEMS_DELETE', {
            from: 'pt-mh-popup-component',
            catId,
            item: itemId,
        });

        if (ptMHHistory) {
            setPtMHHistory((prev) => {
                return {
                    ...prev,
                    [catId]: cloneAndDelete(itemId, ptMHHistory[catId]),
                };
            });
        }
    };

    const onSave = async () => {
        track('CREATE_MH_APPT_INITIATED', { patient_oid });

        if (ptMHHistory && patient_oid) {
            try {
                setIsLoading(true);
                await pushPtMedHistory(
                    getDoc()?._id || '',
                    patient_oid,
                    hasNoMedicalHistory
                        ? {}
                        : {
                              patientMedicalConditions: ptMHHistory.patientMedicalConditions,
                              familyHistory: ptMHHistory.familyHistory,
                              lifestyleHabits: ptMHHistory.lifestyleHabits,
                              recentTravelHistory: ptMHHistory.recentTravelHistory,
                              currentMedications: ptMHHistory.currentMedications,
                              drugAllergy: ptMHHistory.drugAllergy,
                              foodOtherAllergy: ptMHHistory.foodOtherAllergy,
                              pastProcedures: ptMHHistory.pastProcedures,
                              otherMedicalHistory: ptMHHistory.otherMedicalHistory,
                          },
                    new Date().toISOString(),
                );

                setIsLoading(false);
                track('CREATE_MH_APPT_SUCCESS', { patient_oid });
                notification.success({
                    message: 'Patient medical history added',
                    duration: 5,
                    placement: 'bottomRight',
                });

                ekaDispatch({
                    type: ADD_MH_QUEUE,
                    data:
                        !isMHFilled(ptMHHistory) && !hasNoMedicalHistory
                            ? undefined
                            : hasNoMedicalHistory
                            ? {}
                            : ptMHHistory,
                });
            } catch (error) {
                setIsLoading(false);
                notification.error({
                    message: 'Failed to add medical history',
                    duration: 5,
                    placement: 'bottomRight',
                });
                ekaDispatch({
                    type: ADD_MH_QUEUE,
                    data: undefined,
                });
                track('CREATE_MH_APPT_ERROR');
                captureError(error as Error);
            }
        }
    };
    const toggleHasNoMedicalHistory = () => {
        track('NO_KNOWN_MH', {
            hasNoMedicalHistory: (!hasNoMedicalHistory)!.toString(),
        });
        setHasNoMedicalHistory((prev) => !prev);
    };

    const closeMHPop = () => {
        ekaDispatch({
            type: CLOSE_MH_POP,
        });
    };

    return (
        <div className="flex rounded-8 flex-col h-full w-full" id={MEDICAL_HISTORY_CONTAINER}>
            <div className="flex flex-col w-full gap-16">
                <div className="p-16 border-b border-darwin-neutral_new-200 bg-white flex items-center justify-between">
                    <div className="flex items-center gap-16">
                        <p className="text-20 text-darwin-neutral_new-1000 font-400">
                            Link history to ABHA
                        </p>
                        <Checkbox
                            className="mh-add-pt-checkbox text-13"
                            onChange={toggleHasNoMedicalHistory}
                            checked={hasNoMedicalHistory}
                        >
                            No known medical history
                        </Checkbox>
                    </div>
                    <span className="cursor-pointer" onClick={closeMHPop}>
                        {crossIcon('w-12 h-16 fill-darwin-neutral_new-1000')}
                    </span>
                </div>
                <div className="max-h-200 h-200 flex px-24 max-w-736 w-736 overflow-scroll hide-scrollbar">
                    {hasNoMedicalHistory ? (
                        <p className="text-13 font-400 self-center m-auto">
                            Patient has no known medical history
                        </p>
                    ) : (
                        <CommonItemsMH
                            onItemSave={onMedItemSave}
                            onItemDelete={onMedItemDelete}
                            patientHistoryData={ptMHHistory}
                            isMedicalHistorySection={true}
                            medicalHistoryOptions={medicalHistoryOptions}
                            StatusMessage={StatusMessage}
                            medicalHistoryConfigData={medicalHistoryConfigData}
                            onConfigure={() => null}
                            hideCommonItems={false}
                            showMHConfigure={false}
                        />
                    )}
                </div>
            </div>
            <div className="flex flex-col w-full gap-8">
                <div className="flex w-full px-16 pt-8 items-center justify-end">
                    <div
                        className="py-4 px-8 gap-4 items-center rounded-8 cursor-pointer flex justify-between bg-darwin-accent-bright-blue-800 text-white font-700"
                        onClick={onSave}
                    >
                        {isLoading && (
                            <span>
                                <svg
                                    className="w-16 h-16 fill-white font-700 animate-spin"
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 512 512"
                                >
                                    <path d="M96 256c0-26.5-21.5-48-48-48S0 229.5 0 256s21.5 48 48 48S96 282.5 96 256zM108.9 60.89c-26.5 0-48.01 21.49-48.01 47.99S82.39 156.9 108.9 156.9s47.99-21.51 47.99-48.01S135.4 60.89 108.9 60.89zM108.9 355.1c-26.5 0-48.01 21.51-48.01 48.01S82.39 451.1 108.9 451.1s47.99-21.49 47.99-47.99S135.4 355.1 108.9 355.1zM256 416c-26.5 0-48 21.5-48 48S229.5 512 256 512s48-21.5 48-48S282.5 416 256 416zM464 208C437.5 208 416 229.5 416 256s21.5 48 48 48S512 282.5 512 256S490.5 208 464 208zM403.1 355.1c-26.5 0-47.99 21.51-47.99 48.01S376.6 451.1 403.1 451.1s48.01-21.49 48.01-47.99S429.6 355.1 403.1 355.1zM256 0C229.5 0 208 21.5 208 48S229.5 96 256 96s48-21.5 48-48S282.5 0 256 0z"></path>
                                </svg>
                            </span>
                        )}
                        Save changes
                    </div>
                </div>
                <div className="flex mt-4 gap-8 items-center abha-linked-inner-more px-12 py-4">
                    <span className="font-700 text-12">{AbhaFilled('w-18 h-18')}</span>
                    <p className="text-12">Each medical history will be linked</p>
                </div>
            </div>
        </div>
    );
};

export default MHPopup;
